'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

const SemanticRowListContext = createContext(null)

export function useSemanticRowList() {
  return useContext(SemanticRowListContext)
}

/**
 * Shared row-list keyboard nav — stable row ids, data-driven emptiness.
 */
export function SemanticRowListProvider({
  rows,
  isRowEmpty,
  onInsertAfter,
  onRemoveAt,
  children,
}) {
  const apiRef = useRef({ rows, isRowEmpty, onInsertAfter, onRemoveAt })
  apiRef.current = { rows, isRowEmpty, onInsertAfter, onRemoveAt }

  const focusRowById = useCallback((rowId) => {
    requestAnimationFrame(() => {
      const row = document.querySelector(
        `[data-nb-semantic-row][data-nb-row-id="${rowId}"]`,
      )
      const pm = row?.querySelector('.ProseMirror')
      if (pm instanceof HTMLElement) {
        pm.focus()
      }
    })
  }, [])

  const getRowNav = useCallback(
    (rowIndex) => {
      const row = apiRef.current.rows?.[rowIndex]
      const rowId = row?.id
      return {
        index: rowIndex,
        rowId,
        isEmpty: () => {
          const current = apiRef.current.rows?.[rowIndex]
          return Boolean(apiRef.current.isRowEmpty?.(current))
        },
        onEnterEnd: () => {
          if (!rowId) return
          apiRef.current.onInsertAfter?.(rowId)
          const nextRow = apiRef.current.rows?.[rowIndex + 1]
          if (nextRow?.id) focusRowById(nextRow.id)
        },
        onBackspaceEmpty: () => {
          if (!rowId || apiRef.current.rows.length <= 1) return
          const prevRow = apiRef.current.rows?.[rowIndex - 1]
          apiRef.current.onRemoveAt?.(rowId)
          if (prevRow?.id) focusRowById(prevRow.id)
        },
        onArrowDown: () => {
          const nextRow = apiRef.current.rows?.[rowIndex + 1]
          if (nextRow?.id) focusRowById(nextRow.id)
        },
        onArrowUp: () => {
          const prevRow = apiRef.current.rows?.[rowIndex - 1]
          if (prevRow?.id) focusRowById(prevRow.id)
        },
      }
    },
    [focusRowById],
  )

  const value = useMemo(
    () => ({
      getRowNav,
      focusRowById,
    }),
    [getRowNav, focusRowById],
  )

  return (
    <SemanticRowListContext.Provider value={value}>
      {children}
    </SemanticRowListContext.Provider>
  )
}
